import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Filter } from "lucide-react";
import api from "@/lib/api";
import { ParkMap } from "@/components/ParkMap";

export default function Regulations() {
  const [parks, setParks] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/parks").then((r) => setParks(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return parks;
    return parks.filter((p) => p.type === filter);
  }, [parks, filter]);

  const filters = [
    { key: "all", label: "All sites" },
    { key: "wildlife_refuge", label: "Wildlife Refuges" },
    { key: "national_park", label: "National Parks" },
    { key: "other", label: "Other Sites" },
  ];

  return (
    <div>
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="label-eyebrow mb-4">Regulations map</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight mb-6 max-w-4xl">
            Every park and refuge going <span className="italic text-[#C95B3B]">lead-free</span>.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Green markers show waters where lead tackle is prohibited. Orange means biodegradable-only requirements.
            Amber marks proposed or partial rules. Click any pin for details.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
          <div className="flex items-center gap-3 mb-8" data-testid="park-filters">
            <Filter className="w-4 h-4 text-muted-foreground" />
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                  filter === f.key
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent border-border hover:border-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <ParkMap parks={filtered} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
              >
                <Link to={`/parks/${p.slug}`} className="card-flat block p-6 h-full" data-testid={`park-card-${p.slug}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="label-eyebrow" style={{ color: p.type === "wildlife_refuge" ? "#2D5A4C" : p.type === "national_park" ? "#C95B3B" : "#8A8D86" }}>
                      {p.type === "wildlife_refuge" ? "NW Refuge" : p.type === "national_park" ? "National Park" : "Fishing Site"}
                    </div>
                    {p.effective_date && (
                      <div className="text-xs bg-[#F5F2EB] border border-border px-2 py-1 font-mono">
                        {p.effective_date}
                      </div>
                    )}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mb-2">{p.name}</h3>
                  <div className="text-xs text-muted-foreground mb-3">{p.state}</div>
                  <p className="text-sm leading-relaxed mb-4">{p.summary}</p>
                  <span className="text-sm text-[#C95B3B] font-semibold">Details →</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
